import { Button } from '@solinkcorp/solink-ui/components/Button';
import { cn } from '@solinkcorp/solink-ui/lib/utils';
import React from 'react';
import useLocale from 'src/hooks/useLocale';
import { SECONDARY_BUTTON_CLASS } from '../constants';

// Matches the physical Pinpad tablet's layout: row 4 is [0 (double-wide), Clear].
const ROWS: Array<Array<{ digit: string; span?: number } | { clear: true }>> = [
  [{ digit: '1' }, { digit: '2' }, { digit: '3' }],
  [{ digit: '4' }, { digit: '5' }, { digit: '6' }],
  [{ digit: '7' }, { digit: '8' }, { digit: '9' }],
  [{ digit: '0', span: 2 }, { clear: true }],
];

interface KeypadProps {
  onDigit: (digit: string) => void;
  onClear: () => void;
  disabled?: boolean;
}

// h-full w-full: Button's size variants fix a pixel size, which would break the grid's stretch-to-cell layout.
const KEY_CLASSES = cn(
  'h-full w-full rounded-lg heading-display active:bg-interaction-secondary-background-hover',
  SECONDARY_BUTTON_CLASS,
);

const Keypad: React.FC<KeypadProps> = ({ onDigit, onClear, disabled }) => {
  const locale = useLocale();
  return (
    // aspect-[3/4] on the grid keeps cells square without exceeding max-h-full.
    <div className="grid aspect-[3/4] h-auto max-h-full w-full grid-cols-3 grid-rows-4 gap-4">
      {ROWS.flat().map((cell) => {
        if ('clear' in cell) {
          return (
            <Button
              key="clear"
              type="button"
              variant="secondary"
              className={cn(KEY_CLASSES, 'heading-md')}
              data-testid="keypad-clear"
              disabled={disabled}
              onClick={onClear}
            >
              {locale.general.clear}
            </Button>
          );
        }
        return (
          <Button
            key={cell.digit}
            type="button"
            variant="secondary"
            className={cn(KEY_CLASSES, cell.span === 2 && 'col-span-2')}
            data-testid={`keypad-key-${cell.digit}`}
            disabled={disabled}
            onClick={() => onDigit(cell.digit)}
          >
            {cell.digit}
          </Button>
        );
      })}
    </div>
  );
};

export default Keypad;
